import { api } from '@xtnd/web/shared/api';
import {
  ArInvoiceAttorneyResponsesResponse,
  AttorneyResponsePatchPayload,
  AttorneyResponsePayload,
  DelegateUpdatePayload,
  Invoice,
  InvoiceResponse,
} from '../types';
import { get } from 'lodash';

// ========== Invoice Search Types ==========
export interface InvoiceSearchParams {
  invoiceNumber?: string;
  limit?: number;
}

export interface InvoiceSearchItem {
  dayPastDue: number;
  matterId: string;
  matterDisplayNumber: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
}

export interface InvoiceSearchResponse {
  data: InvoiceSearchItem[];
}

// ========== Bulk Assign Types ==========
export interface BulkAssignAppealsClerkPayload {
  invoiceIds: string[];
  assignedTo?: string | null;
}

export interface BulkAssignAppealsClerkResponse {
  data: {
    successCount: number;
    errorCount: number;
    errors: string[];
  };
}

/**
 * Service for managing invoice API requests related to AR Collections.
 * Handles retrieval and management of invoices in the system.
 */
class InvoiceApiService {
  private readonly baseResource = '/ar-collections';
  private readonly baseService = '/invoices';

  /**
   * Fetches a single invoice by its unique identifier.
   *
   * @param invoiceId - The unique identifier of the invoice.
   * @returns A promise that resolves with the Invoice object.
   *
   * @example
   * const invoice = await invoiceApi.getInvoice('INV-1234');
   */
  async getInvoice(invoiceId: string): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}`;
    const response = await api.get<InvoiceResponse>(endpoint);
    return response.data;
  }

  /**
   * Fetches the attorney response for a given invoice and appeal.
   *
   * @param invoiceId - The unique identifier of the invoice.
   * @param appealId - The unique identifier of the appeal.
   * @returns A promise that resolves with the Invoice object.
   *
   * @example
   * const attorneyResponse = await invoiceApi.getAttorneyResponse('INV-1234', 'APL-1234');
   */
  async getAttorneyResponse(invoiceId: string, appealId: string): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/appeal/${appealId}/attorney-responses`;
    const response = await api.get<InvoiceResponse>(endpoint);
    return response.data;
  }

  /**
   * Submits an attorney response for a given invoice and appeal.
   *
   * @param tenantId - The unique identifier of the tenant.
   * @param email - The email address of the attorney/timekeeper.
   * @param invoiceId - The unique identifier of the invoice.
   * @param appealId - The unique identifier of the appeal.
   * @param payload - The attorney response payload.
   * @returns A promise that resolves with the Invoice object.
   */
  async submitAttorneyResponse(
    invoiceId: string,
    appealId: string,
    payload: AttorneyResponsePayload
  ): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/appeal/${appealId}/attorney-responses`;
    const response = await api.post<InvoiceResponse>(endpoint, payload);
    return response.data;
  }

  /**
   * Submits an attorney response for a given invoice and appeal.
   *
   * @param tenantId - The unique identifier of the tenant.
   * @param email - The email address of the attorney/timekeeper.
   * @param invoiceId - The unique identifier of the invoice.
   * @param appealId - The unique identifier of the appeal.
   * @param payload - The attorney response payload.
   * @returns A promise that resolves with the Invoice object.
   */
  async updateAttorneyResponse(
    invoiceId: string,
    appealId: string,
    timekeeperId: string,
    payload: AttorneyResponsePayload
  ): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/appeal/${appealId}/attorney-responses/${timekeeperId}`;
    const response = await api.put<InvoiceResponse>(endpoint, payload);
    return response.data;
  }

  /**
   * Submits an attorney response for a given invoice and appeal.
   *
   * @param tenantId - The unique identifier of the tenant.
   * @param email - The email address of the attorney/timekeeper.
   * @param invoiceId - The unique identifier of the invoice.
   * @param appealId - The unique identifier of the appeal.
   * @param payload - The attorney response payload.
   * @returns A promise that resolves with the Invoice object.
   */
  async patchAttorneyResponse(
    invoiceId: string,
    appealId: string,
    timekeeperId: string,
    payload: AttorneyResponsePatchPayload
  ): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/appeal/${appealId}/attorney-responses/${timekeeperId}`;
    const response = await api.patch<InvoiceResponse>(endpoint, payload);
    return response.data;
  }

  /**
   * Fetches the attorney responses for a given invoice.
   *
   * @param invoiceId - The unique identifier of the invoice.
   * @returns A promise that resolves with the ArInvoiceAttorneyResponsesResponse object.
   *
   * @example
   * const attorneyResponses = await invoiceApi.getAttorneyResponses('INV-1234');
   */
  async getAttorneyResponses(invoiceId: string): Promise<ArInvoiceAttorneyResponsesResponse> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/attorney-responses`;
    const response = await api.get<InvoiceResponse>(endpoint);
    const data = get(response, 'data', {}) as ArInvoiceAttorneyResponsesResponse;
    return data;
  }

  /**
   * Search invoices by invoice number
   * GET /ar-collections/invoices/recently
   *
   * @param params - Search parameters (invoiceNumber, limit)
   * @returns A promise that resolves with InvoiceSearchResponse
   *
   * @example
   * const results = await invoiceApi.searchInvoices({ invoiceNumber: '1234', limit: 5 });
   */
  async searchInvoices(params: InvoiceSearchParams): Promise<InvoiceSearchResponse> {
    const queryParams = new URLSearchParams();

    if (params.invoiceNumber?.trim()) {
      queryParams.append('invoiceNumber', params.invoiceNumber.trim());
    }

    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const query = queryParams.toString();
    const endpoint = `${this.baseResource}${this.baseService}/recently${query ? `?${query}` : ''}`;

    return await api.get<InvoiceSearchResponse>(endpoint);
  }

  /**
   * Rollback an invoice from APPEAL_SUBMITTED (BACKLOG) to PRE_APPEAL.
   *
   * @param invoiceId - The unique identifier of the invoice.
   * @returns A promise that resolves with the updated appeal data.
   *
   * @example
   * const result = await invoiceApi.rollbackInvoice('INV-1234');
   */
  async rollbackInvoice(invoiceId: string): Promise<{ data: unknown }> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/rollback`;
    const response = await api.patch<{ data: unknown }>(endpoint);
    return response;
  }

  /**
   * Bulk assign or unassign Appeals Clerk to/from multiple invoices.
   *
   * @param payload - Object containing invoiceIds and assignedTo email (null to unassign)
   * @returns A promise that resolves with success/error counts
   *
   * @example
   * const result = await invoiceApi.bulkAssignAppealsClerk({
   *   invoiceIds: ['INV-1', 'INV-2'],
   *   assignedTo: 'clerk@example.com'
   * });
   */
  async bulkAssignAppealsClerk(
    payload: BulkAssignAppealsClerkPayload
  ): Promise<BulkAssignAppealsClerkResponse> {
    const endpoint = `${this.baseResource}${this.baseService}/assign`;
    return await api.post<BulkAssignAppealsClerkResponse>(endpoint, payload);
  }

  /**
   * Updates the delegate for a specific timekeeper in an appeal.
   *
   * @param invoiceId - The unique identifier of the invoice.
   * @param appealId - The unique identifier of the appeal.
   * @param timekeeperId - The unique identifier of the timekeeper.
   * @param payload - The delegate update payload (delegate name and email, or null to clear).
   * @returns A promise that resolves with the updated Invoice object.
   */
  async updateDelegate(
    invoiceId: string,
    appealId: string,
    timekeeperId: string,
    payload: DelegateUpdatePayload
  ): Promise<Invoice> {
    const endpoint = `${this.baseResource}${this.baseService}/${invoiceId}/appeal/${appealId}/attorney-responses/${timekeeperId}/delegate`;
    const response = await api.patch<InvoiceResponse>(endpoint, payload);
    return response.data;
  }
}

/**
 * Singleton instance of the InvoiceApiService for use in AR Collections features.
 */
export const invoiceApi = new InvoiceApiService();
